import { DEFAULT_TEMPLATE_FORM_LANGUAGE } from '@common/i18n/constants';
import { getFormValidationTranslations } from '@common/i18n/formValidation';
import type { QuestionSchema, QuestionValidationSchema } from '@common/types/templateFormFields';
import type { TemplateFormLanguage } from '@common/types/templateForms';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import RequiredAsterisk from '@/components/RequiredAsterisk';
import FormFieldDescription from './FormFieldDescription';

interface Props {
    schema: QuestionSchema;
    label: string;
    validation?: QuestionValidationSchema | null;
    labelStyles?: object;
    disabled?: boolean;
    shouldRenderAsterisk?: boolean;
    shouldRenderCritical?: boolean;
    isCriticalQuestion?: boolean;
    includeFieldAutomationRequired?: boolean;
    isRequiredForFullAutomation?: boolean;
    opacity?: string;
    language?: TemplateFormLanguage;
}

export default function FormFieldLabel({
    schema,
    label,
    validation,
    labelStyles,
    disabled,
    shouldRenderAsterisk = true,
    shouldRenderCritical = true,
    isCriticalQuestion,
    includeFieldAutomationRequired = false,
    isRequiredForFullAutomation,
    opacity = '1',
    language = DEFAULT_TEMPLATE_FORM_LANGUAGE,
}: Props) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const t = getFormValidationTranslations(language);

    return (
        <Box sx={{ opacity }}>
            <Typography
                variant={isMobile ? 'body2' : 'body1'}
                style={{
                    color: disabled ? theme.palette.neutral.gray : theme.palette.neutral.black,
                    ...labelStyles,
                }}
            >
                {shouldRenderAsterisk &&
                (validation?.required ||
                    schema.isRequiredForFullAutomation ||
                    (includeFieldAutomationRequired && isRequiredForFullAutomation)) ? (
                    <RequiredAsterisk />
                ) : null}
                {label}
                {shouldRenderCritical && isCriticalQuestion && (
                    <Box sx={{ display: 'inline', marginLeft: '4px' }}>
                        <Tooltip title={t.criticalQuestionTooltip} arrow placement="top-start">
                            <ErrorOutlineIcon
                                sx={{
                                    fontSize: '16px',
                                    color: theme.palette.success.main,
                                }}
                            />
                        </Tooltip>
                    </Box>
                )}
            </Typography>
            {schema.description && <FormFieldDescription description={schema.description} />}
        </Box>
    );
}
